import type {
  ApiEnvelope,
  PagedData,
  ConsumedRow,
  ExceptionRow,
  PayloadRow,
  ServerRow,
  ServerWritePayload,
  StatsResponse,
  UserRow,
  WhoAmI,
  ScanRow,
  ScanPidRow,
  QueueRow,
  QueueFailedRow,
  RecentActivity,
  TraceResponse,
  SmtpRow,
  SmtpWritePayload,
  AccountEmailRow,
  AppSettings,
  NotificationConfigRow,
} from "./types";
import type {
  StorageVendor,
  VendorCreatePayload,
  VendorUpdatePayload,
  VendorRole,
  VendorTestResult,
  StoragePolicies,
  SessionConfig,
  ActiveSession,
  MonitoringStatsResponse,
  StorageException,
} from "./components/storageRelay/types";
import type {
  DispatchLogListResponse,
  DispatchLogEntry,
  SiteCredentialsResponse,
  HealthResponse,
  DlqResponse,
  PerSiteHealthResponse,
  StatsResponse as ScanDispatchStatsResponse,
  AuditLogResponse,
  TestLoginResponse,
  PluginTesterResponse,
  EventType,
} from "./components/scanDispatch/types";

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  "https://cf-corewatch-api.weathered-mouse-692a.workers.dev";

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  return apiRequest<T>("GET", path, undefined, signal);
}

async function apiDelete<T>(path: string, signal?: AbortSignal): Promise<T> {
  return apiRequest<T>("DELETE", path, undefined, signal);
}

async function apiPost<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return apiRequest<T>("POST", path, body, signal);
}

async function apiPatch<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return apiRequest<T>("PATCH", path, body, signal);
}

async function apiPut<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return apiRequest<T>("PUT", path, body, signal);
}

// Hook called by the auth provider when a 401 is observed mid-session. The
// SPA wires this so it can flip its state to anonymous and render OtpLogin.
type Unauthorized = () => void;
let onUnauthorized: Unauthorized | null = null;
export function setUnauthorizedHandler(fn: Unauthorized | null) { onUnauthorized = fn; }

async function apiRequest<T>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: "include",
    signal,
  };
  if (body !== undefined) {
    init.headers = { "Content-Type": "application/json" };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(`${API_BASE}${path}`, init);
  const respBody = (await res.json().catch(() => null)) as ApiEnvelope<T> | null;
  if (res.status === 401 || res.status === 403) {
    // whoami is allowed to be 401 (anonymous boot). Other 401s mean the
    // session expired — bounce the SPA back to the login screen.
    if (!path.startsWith("/api/whoami") && !path.startsWith("/api/auth/")) {
      onUnauthorized?.();
    }
    throw new ApiError(respBody?.message ?? "Not authenticated.", res.status);
  }
  if (!respBody) throw new ApiError(`Empty response from ${path}`, res.status);
  if (!res.ok || respBody.code.startsWith("4") || respBody.code.startsWith("5")) {
    throw new ApiError(respBody.message || `Request failed (${res.status})`, res.status);
  }
  return respBody.data as T;
}

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length > 0 ? `?${parts.join("&")}` : "";
}

export const api = {
  whoami: (signal?: AbortSignal) => apiGet<WhoAmI>("/api/whoami", signal),
  stats: (signal?: AbortSignal) => apiGet<StatsResponse>("/api/stats", signal),
  recentActivity: (limit = 20, signal?: AbortSignal) =>
    apiGet<RecentActivity>(`/api/recent-activity?limit=${limit}`, signal),
  recentActivityPaged: (
    p: Record<string, string | number | undefined>,
    signal?: AbortSignal,
  ) => apiGet<RecentActivity>(`/api/recent-activity${buildQuery(p)}`, signal),
  trace: (pid: string, signal?: AbortSignal) =>
    apiGet<TraceResponse>(`/api/trace/${encodeURIComponent(pid)}`, signal),

  payloads: {
    list: (p: Record<string, string | number | undefined>, signal?: AbortSignal) =>
      apiGet<PagedData<PayloadRow>>(`/api/payloads${buildQuery(p)}`, signal),
    get: (id: number, signal?: AbortSignal) =>
      apiGet<PayloadRow>(`/api/payloads/${id}`, signal),
  },
  consumed: {
    list: (p: Record<string, string | number | undefined>, signal?: AbortSignal) =>
      apiGet<PagedData<ConsumedRow>>(`/api/consumed${buildQuery(p)}`, signal),
    get: (id: number, signal?: AbortSignal) =>
      apiGet<ConsumedRow>(`/api/consumed/${id}`, signal),
  },
  exceptions: {
    list: (p: Record<string, string | number | undefined>, signal?: AbortSignal) =>
      apiGet<PagedData<ExceptionRow>>(`/api/exceptions${buildQuery(p)}`, signal),
    get: (id: number, signal?: AbortSignal) =>
      apiGet<ExceptionRow>(`/api/exceptions/${id}`, signal),
  },
  servers: {
    list: (signal?: AbortSignal) => apiGet<ServerRow[]>("/api/servers", signal),
    get: (id: number, signal?: AbortSignal) =>
      apiGet<ServerRow>(`/api/servers/${id}`, signal),
    setActive: (id: number, isActive: boolean, signal?: AbortSignal) =>
      apiPatch<ServerRow>(`/api/servers/${id}/active`, { is_active: isActive }, signal),
    create: (body: ServerWritePayload, signal?: AbortSignal) =>
      apiPost<ServerRow>(`/api/servers`, body, signal),
    update: (id: number, body: ServerWritePayload, signal?: AbortSignal) =>
      apiPut<ServerRow>(`/api/servers/${id}`, body, signal),
    delete: (id: number, signal?: AbortSignal) =>
      apiDelete<{ id: string; table: string; changes: number }>(`/api/servers/${id}`, signal),
  },
  users: {
    list: (p: Record<string, string | number | undefined>, signal?: AbortSignal) =>
      apiGet<PagedData<UserRow>>(`/api/users${buildQuery(p)}`, signal),
    get: (id: number, signal?: AbortSignal) =>
      apiGet<UserRow>(`/api/users/${id}`, signal),
  },
  scan: {
    list: (p: Record<string, string | number | undefined>, signal?: AbortSignal) =>
      apiGet<PagedData<ScanRow>>(`/api/scan${buildQuery(p)}`, signal),
    get: (id: number, signal?: AbortSignal) =>
      apiGet<ScanRow>(`/api/scan/${id}`, signal),
  },
  scanPids: {
    list: (p: Record<string, string | number | undefined>, signal?: AbortSignal) =>
      apiGet<PagedData<ScanPidRow>>(`/api/scan_pids${buildQuery(p)}`, signal),
    get: (id: number, signal?: AbortSignal) =>
      apiGet<ScanPidRow>(`/api/scan_pids/${id}`, signal),
  },
  queues: {
    list: (p: Record<string, string | number | undefined>, signal?: AbortSignal) =>
      apiGet<PagedData<QueueRow>>(`/api/queues${buildQuery(p)}`, signal),
    get: (id: number, signal?: AbortSignal) =>
      apiGet<QueueRow>(`/api/queues/${id}`, signal),
  },
  queuesFailed: {
    list: (p: Record<string, string | number | undefined>, signal?: AbortSignal) =>
      apiGet<PagedData<QueueFailedRow>>(`/api/queues_failed${buildQuery(p)}`, signal),
    get: (id: number, signal?: AbortSignal) =>
      apiGet<QueueFailedRow>(`/api/queues_failed/${id}`, signal),
  },

  auth: {
    requestOtp: (email: string, signal?: AbortSignal) =>
      apiPost<{ delivery: "smtp" | "bootstrap" }>(`/api/auth/request-otp`, { email }, signal),
    verifyOtp: (email: string, code: string, signal?: AbortSignal) =>
      apiPost<{ email: string; expires_in_seconds: number; session_timeout_minutes: number }>(
        `/api/auth/verify-otp`, { email, code }, signal,
      ),
    logout: (signal?: AbortSignal) =>
      apiPost<null>(`/api/auth/logout`, {}, signal),
  },

  smtp: {
    get: (signal?: AbortSignal) => apiGet<SmtpRow | null>(`/api/smtp`, signal),
    save: (body: SmtpWritePayload, signal?: AbortSignal) =>
      apiPut<SmtpRow>(`/api/smtp`, body, signal),
    test: (to: string, signal?: AbortSignal) =>
      apiPost<{ server_response: string }>(`/api/smtp/test`, { to }, signal),
  },

  account: {
    listEmails: (signal?: AbortSignal) => apiGet<AccountEmailRow[]>(`/api/account-emails`, signal),
    addEmail: (email: string, signal?: AbortSignal) =>
      apiPost<AccountEmailRow>(`/api/account-emails`, { email }, signal),
    updateEmail: (id: number, email: string, signal?: AbortSignal) =>
      apiPut<AccountEmailRow>(`/api/account-emails/${id}`, { email }, signal),
    deleteEmail: (id: number, signal?: AbortSignal) =>
      apiDelete<{ id: string; changes: number }>(`/api/account-emails/${id}`, signal),
    getSettings: (signal?: AbortSignal) => apiGet<AppSettings>(`/api/app-settings`, signal),
    saveSettings: (body: Partial<AppSettings>, signal?: AbortSignal) =>
      apiPut<AppSettings>(`/api/app-settings`, body, signal),
  },

  notifications: {
    list: (signal?: AbortSignal) =>
      apiGet<NotificationConfigRow[]>(`/api/notifications`, signal),
    update: (module: string, body: Partial<{
      enabled: boolean;
      threshold: number;
      window_minutes: number;
    }>, signal?: AbortSignal) =>
      apiPut<NotificationConfigRow>(`/api/notifications/${module}`, body, signal),
  },

  storageRelay: {
    vendors: {
      list: (signal?: AbortSignal) =>
        apiGet<{ vendors: StorageVendor[] }>(`/api/storage-relay/vendors`, signal),
      create: (body: VendorCreatePayload, signal?: AbortSignal) =>
        apiPost<{ vendor: StorageVendor }>(`/api/storage-relay/vendors`, body, signal),
      update: (id: string, body: VendorUpdatePayload, signal?: AbortSignal) =>
        apiPut<{ vendor: StorageVendor }>(`/api/storage-relay/vendors/${id}`, body, signal),
      delete: (id: string, signal?: AbortSignal) =>
        apiDelete<null>(`/api/storage-relay/vendors/${id}`, signal),
      setRole: (id: string, role: VendorRole, enabled = true, signal?: AbortSignal) =>
        apiPost<{ vendor: StorageVendor }>(`/api/storage-relay/vendors/${id}/role`, { role, enabled }, signal),
      test: (id: string, signal?: AbortSignal) =>
        apiPost<VendorTestResult>(`/api/storage-relay/vendors/${id}/test`, {}, signal),
    },
    policies: {
      get: (signal?: AbortSignal) =>
        apiGet<{ policies: StoragePolicies; sessions: SessionConfig }>(`/api/storage-relay/policies`, signal),
      save: (body: StoragePolicies, signal?: AbortSignal) =>
        apiPut<{ policies: StoragePolicies; sessions: SessionConfig }>(`/api/storage-relay/policies`, body, signal),
    },
    sessions: {
      getConfig: (signal?: AbortSignal) =>
        apiGet<{ sessions: SessionConfig }>(`/api/storage-relay/sessions/config`, signal),
      saveConfig: (body: SessionConfig, signal?: AbortSignal) =>
        apiPut<{ sessions: SessionConfig }>(`/api/storage-relay/sessions/config`, body, signal),
      list: (signal?: AbortSignal) =>
        apiGet<{ sessions: ActiveSession[] }>(`/api/storage-relay/sessions`, signal),
      revoke: (id: string, signal?: AbortSignal) =>
        apiDelete<null>(`/api/storage-relay/sessions/${id}`, signal),
      revokeAll: (signal?: AbortSignal) =>
        apiPost<{ revokedCount: number }>(`/api/storage-relay/sessions/revoke-all`, {}, signal),
    },
    monitoring: {
      stats: (signal?: AbortSignal) =>
        apiGet<MonitoringStatsResponse>(`/api/storage-relay/monitoring/stats`, signal),
      exceptions: (limit = 50, signal?: AbortSignal) =>
        apiGet<{ exceptions: StorageException[] }>(`/api/storage-relay/monitoring/exceptions?limit=${limit}`, signal),
    },
  },

  // Scan Dispatch admin (corewatch dashboard → Settings → Scan Dispatch).
  // Backed by cf-d1-api-v2 /v1/scan-dispatch/* via cf-corewatch-api-v2 proxy.
  // Rollback: deleting this whole `scanDispatch` block + the matching SPA
  // components leaves the rest of the api object untouched.
  scanDispatch: {
    log: {
      list: (params: { status?: string; website?: string; q?: string; from?: string; to?: string; limit?: number; offset?: number } = {}, signal?: AbortSignal) => {
        const qs = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== "") qs.set(k, String(v)); });
        const query = qs.toString();
        return apiGet<DispatchLogListResponse>(`/api/scan-dispatch/log${query ? `?${query}` : ""}`, signal);
      },
      get: (scanJobId: string, signal?: AbortSignal) =>
        apiGet<{ entry: DispatchLogEntry & Record<string, unknown> }>(`/api/scan-dispatch/log/${encodeURIComponent(scanJobId)}`, signal),
      resend: (scanJobId: string, signal?: AbortSignal) =>
        apiPost<{ scanJobId: string; websiteUrl: string; message: string }>(`/api/scan-dispatch/log/${encodeURIComponent(scanJobId)}/resend`, {}, signal),
      delete: (scanJobId: string, signal?: AbortSignal) =>
        apiDelete<{ scanJobId: string }>(`/api/scan-dispatch/log/${encodeURIComponent(scanJobId)}`, signal),
      deleteAll: (params: { status?: string; website?: string; q?: string; from?: string; to?: string; confirm_all?: boolean } = {}, signal?: AbortSignal) => {
        const qs = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== "" && v !== false) qs.set(k, String(v)); });
        const query = qs.toString();
        return apiDelete<{ deleted: number }>(`/api/scan-dispatch/log${query ? `?${query}` : ""}`, signal);
      },
    },
    credentials: {
      list: (params: { q?: string; limit?: number; offset?: number } = {}, signal?: AbortSignal) => {
        const qs = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== "") qs.set(k, String(v)); });
        const query = qs.toString();
        return apiGet<SiteCredentialsResponse>(`/api/scan-dispatch/credentials${query ? `?${query}` : ""}`, signal);
      },
      invalidateTokens: (websiteUrl: string, signal?: AbortSignal) =>
        apiPost<{ websiteUrl: string }>(`/api/scan-dispatch/credentials/invalidate-tokens`, { website_url: websiteUrl }, signal),
      rebootstrap: (websiteUrl: string, signal?: AbortSignal) =>
        apiPost<{ websiteUrl: string }>(`/api/scan-dispatch/credentials/rebootstrap`, { website_url: websiteUrl }, signal),
      testLogin: (websiteUrl: string, signal?: AbortSignal) =>
        apiPost<TestLoginResponse>(`/api/scan-dispatch/credentials/test-login`, { website_url: websiteUrl }, signal),
      deleteAll: (signal?: AbortSignal) =>
        apiDelete<{ deleted: number }>(`/api/scan-dispatch/credentials?confirm_all=true`, signal),
    },
    health: (signal?: AbortSignal) => apiGet<HealthResponse>(`/api/scan-dispatch/health`, signal),
    dlq: {
      list: (limit = 100, signal?: AbortSignal) =>
        apiGet<DlqResponse>(`/api/scan-dispatch/dlq?limit=${limit}`, signal),
      requeue: (scanJobId: string, signal?: AbortSignal) =>
        apiPost<{ scanJobId: string }>(`/api/scan-dispatch/dlq/${encodeURIComponent(scanJobId)}/requeue`, {}, signal),
    },
    perSiteHealth: (window: "24h" | "7d" | "30d" = "7d", signal?: AbortSignal) =>
      apiGet<PerSiteHealthResponse>(`/api/scan-dispatch/per-site-health?window=${window}`, signal),
    stats: (window: "24h" | "7d" | "30d" = "24h", signal?: AbortSignal) =>
      apiGet<ScanDispatchStatsResponse>(`/api/scan-dispatch/stats?window=${window}`, signal),
    auditLog: (limit = 100, signal?: AbortSignal) =>
      apiGet<AuditLogResponse>(`/api/scan-dispatch/audit-log?limit=${limit}`, signal),
    auditLogDelete: (id: number | string, signal?: AbortSignal) =>
      apiDelete<{ deleted: number }>(`/api/scan-dispatch/audit-log/${encodeURIComponent(String(id))}`, signal),
    auditLogClear: (signal?: AbortSignal) =>
      apiDelete<{ deleted: number }>(`/api/scan-dispatch/audit-log?confirm_all=true`, signal),
    pluginTest: (websiteUrl: string, eventType: EventType, data: Record<string, unknown>, signal?: AbortSignal) =>
      apiPost<PluginTesterResponse>(`/api/scan-dispatch/plugin-test`, {
        website_url: websiteUrl, event_type: eventType, data,
      }, signal),
  },

  admin: {
    deleteRow: (table: string, id: number | string, signal?: AbortSignal) =>
      apiDelete<{ table: string; id: string; changes: number }>(
        `/api/admin/${encodeURIComponent(table)}/${encodeURIComponent(String(id))}`,
        signal,
      ),
    updateRow: (
      table: string,
      id: number | string,
      patch: Record<string, unknown>,
      signal?: AbortSignal,
    ) =>
      apiPatch<{ table: string; id: string; changes: number; rejected?: string[] }>(
        `/api/admin/${encodeURIComponent(table)}/${encodeURIComponent(String(id))}`,
        patch,
        signal,
      ),
    truncate: (table: string, signal?: AbortSignal) =>
      apiPost<{ table: string; deleted: number; reported_changes: number }>(
        `/api/admin/${encodeURIComponent(table)}/truncate`,
        {},
        signal,
      ),
    flush: (tables: string[], signal?: AbortSignal) =>
      apiPost<{
        results: Array<{ table: string; deleted: number; ok: boolean; error?: string }>;
      }>(`/api/admin/flush`, { tables }, signal),
    export: (tables: string[], signal?: AbortSignal) =>
      apiGet<{
        format_version: number;
        exported_at: string;
        tables: Record<string, Array<Record<string, unknown>>>;
        errors?: Record<string, string>;
      }>(`/api/admin/export?tables=${encodeURIComponent(tables.join(","))}`, signal),
    import: (
      body: {
        format_version?: number;
        tables: Record<string, Array<Record<string, unknown>>>;
        mode?: "append" | "replace";
      },
      signal?: AbortSignal,
    ) =>
      apiPost<{
        results: Array<{ table: string; inserted: number; ok: boolean; error?: string }>;
        mode: "append" | "replace";
      }>(`/api/admin/import`, body, signal),
    tables: (signal?: AbortSignal) =>
      apiGet<{ tables: Array<{ name: string; pk: string; columns: string[] }> }>(
        `/api/admin/tables`,
        signal,
      ),
  },
};
